<template>
    <section class="section admin-welcome-page is-full-height-with-navbar">
        <div class="container" v-if="initDone">
            <div class="columns">
                <div class="column mb-5 has-text-centered">
                    <h1 class="title mb-2">{{ basicEventData?.fullName }}</h1>
                    <p v-if="basicEventData?.federation" class="is-size-7">
                        <span class="has-text-grey-light">{{ translate("page.admin_welcome.federation") }} </span>
                        <a
                            v-if="basicEventData.federation.canAccessManagement"
                            :href="urls.federations().manageFederation(basicEventData.federation.id)"
                        >
                            {{ basicEventData.federation.name }}
                        </a>
                        <span v-else>{{ basicEventData.federation.name }}</span>
                        <a
                            v-if="userRole === 'admin'"
                            :href="urls.superAdminUrls().manageFederation(basicEventData.federation.id, basicEventData.federation.name)"
                            class="ml-2"
                        >
                            <span class="icon"><i class="fas fa-wrench"></i></span>
                        </a>
                    </p>
                </div>
            </div>

            <MainStatsBar :basic-event-data="basicEventData" :main-stats="mainStats" />

            <ShortcutsBar :event-id="eventId" :bundles="bundles" :has-broad-access="hasBroadAccess" />

            <div class="columns" v-if="hasBroadAccess">
                <div class="column">
                    <div class="is-flex mb-3 is-align-items-center">
                        <p class="is-size-6-mobile is-size-4 is-flex-grow-1">
                            {{ translate("page.admin_welcome.event_checklist") }}
                        </p>
                        <p class="is-size-7-mobile is-size-6">
                            <Checkbox2 v-model="hideCompletedTasks">
                                {{ translate("page.admin_welcome.hide_completed_tasks") }}
                            </Checkbox2>
                        </p>
                    </div>

                    <template v-if="filteredActionableItems.length > 0">
                        <div
                            class="box checklist"
                            v-for="groupWithActionItems in filteredActionableItems"
                            :key="groupWithActionItems.group"
                        >
                            <div class="is-size-6-mobile is-size-5 has-text-weight-bold mb-3">
                                {{ translate(groupWithActionItems.group) }}
                            </div>

                            <hr class="is-grey-dark" />

                            <ActionableItemElement
                                :event-id="eventId"
                                :actionable-item="actionableItem"
                                v-for="actionableItem in groupWithActionItems.actionableItems"
                                :key="actionableItem.actionName"
                            />
                        </div>
                    </template>
                    <div v-else class="column is-offset-2 is-8">
                        <Notification class="is-success"> 🎉 {{ translate("page.admin_welcome.all_tasks_completed") }} </Notification>
                    </div>
                </div>
            </div>
        </div>
        <div class="container" v-else>
            <div class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </div>

        <EventCostModal ref="eventCostModal" :event-id="eventId" :user-role="userRole" />
    </section>
</template>
<script setup lang="ts">
    import Checkbox2 from "../Common/Checkbox2.vue";
    import { MainStats, adminEventSummaryApi, BasicEventData, ActionableItem } from "../Api/adminEventSummaryApi";
    import { Bundle } from "../Common/consts";
    import { computed, onMounted, ref, watch } from "vue";
    import ActionableItemElement from "./ActionableItemElement.vue";
    import EventCostModal from "../Admin/DetailModals/EventCostModal.vue";
    import localStorage from "../Common/store";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { eventBus, ON_EVENT_COST_BREAKDOWN_MODAL_OPEN, REFRESH_SUMMARY } from "./eventBus";
    import ShortcutsBar from "./ShortcutsBar.vue";
    import MainStatsBar from "./MainStatsBar.vue";
    import Notification from "../Common/Notification.vue";
    import { UserRole } from "../Common/types";
    import urls from "../Urls/urls";
    import { useScrollResetOnMount } from "../Common/useScrollResetOnMount";

    useScrollResetOnMount();

    const props = defineProps<{
        eventStatsUrl: string;
        eventId: number;
        userRole: UserRole;
    }>();

    const hideCompletedTasks = ref(false);
    watch(hideCompletedTasks, value => {
        localStorage.setBoolean("hideCompletedTasks_" + props.eventId, value);
    });

    onMounted(() => {
        hideCompletedTasks.value = localStorage.getBoolean("hideCompletedTasks_" + props.eventId);
    });

    type ActionableItemsForGroup = { group: string; actionableItems: ActionableItem[] };

    const initDone = ref(false);
    const basicEventData = ref<BasicEventData | null>(null);
    const mainStats = ref<MainStats | null>(null);
    const actionableItemsByActionGroup = ref<ActionableItemsForGroup[]>([]);
    const bundles = ref<Bundle[]>([]);
    const hasBroadAccess = ref(false);

    const filteredActionableItems = computed<ActionableItemsForGroup[]>(() => {
        const newResult: ActionableItemsForGroup[] = [];
        for (const groupWithActionItems of actionableItemsByActionGroup.value) {
            const newActionableItems = groupWithActionItems.actionableItems.filter(
                actionableItem => !hideCompletedTasks.value || actionableItem.status !== "done",
            );
            if (newActionableItems.length > 0) {
                newResult.push({ actionableItems: newActionableItems, group: groupWithActionItems.group });
            }
        }

        return newResult;
    });

    function fetchAdminEventSummary() {
        return adminEventSummaryApi
            .getAdminEventSummary(props.eventId)
            .then(response => {
                basicEventData.value = response.basicEventData;
                mainStats.value = response.mainStats;
                bundles.value = response.bundles;
                hasBroadAccess.value = response.hasBroadAccess;

                actionableItemsByActionGroup.value = response.actionableItems.reduce(
                    (acc, item) => {
                        const group = item.actionGroup;
                        const groupIndex = acc.findIndex(g => g.group === group);
                        if (groupIndex === -1) {
                            acc.push({ group, actionableItems: [item] });
                        } else {
                            acc[groupIndex].actionableItems.push(item);
                        }
                        return acc;
                    },
                    [] as { group: string; actionableItems: ActionableItem[] }[],
                );
            })
            .catch(handleError);
    }

    const eventCostModal = ref<InstanceType<typeof EventCostModal> | null>(null);

    function openEventCostModal() {
        eventCostModal.value?.open();
    }

    onMounted(() => {
        eventBus.$on(ON_EVENT_COST_BREAKDOWN_MODAL_OPEN, openEventCostModal);
        eventBus.$on(REFRESH_SUMMARY, fetchAdminEventSummary);

        initDone.value = false;
        return fetchAdminEventSummary().finally(() => {
            initDone.value = true;
        });
    });
</script>

<style scoped></style>
